import React, { useState, useEffect } from 'react'
import gliaInject from '../../../Helpers/gliaInject'

import WellbySelect from '../../Utilities/WellbySelectDropdown/WellbySelectDropdownComponent'

import PlusSVG from 'SVGs/plus.svg'
import XMarkSVG from 'SVGs/x-mark.svg'

// Import all calculator components
import AutoLoanCalculatorBlock from 'Blocks/Calculators/AutoLoanCalculatorBlock'
import HELOCCalculatorBlock from 'Blocks/Calculators/HELOCCalculatorBlock'
import HomeRefinanceCalculatorBlock from 'Blocks/Calculators/HomeRefinanceCalculatorBlock'
import LoantermandDebtConsolidationCalculatorBlock from 'Blocks/Calculators/LoantermandDebtConsolidationCalculatorBlock'
import MortgageCalculatorBlock from 'Blocks/Calculators/MortgageCalculatorBlock'
import RetirementCalculatorBlock from 'Blocks/Calculators/RetirementCalculatorBlock'
import SavingInterestCalculatorBlock from 'Blocks/Calculators/SavingInterestCalculatorBlock'
import SavingsGoalCalculatorBlock from 'Blocks/Calculators/SavingsGoalCalculatorBlock'

const CalculatorComponentMap = {
  'AutoLoanCalculatorBlock': AutoLoanCalculatorBlock,
  'HELOCCalculatorBlock': HELOCCalculatorBlock,
  'HomeRefinanceCalculatorBlock': HomeRefinanceCalculatorBlock,
  'LoantermandDebtConsolidationCalculatorBlock': LoantermandDebtConsolidationCalculatorBlock,
  'MortgageCalculatorBlock': MortgageCalculatorBlock,
  'RetirementCalculatorBlock': RetirementCalculatorBlock,
  'SavingInterestCalculatorBlock': SavingInterestCalculatorBlock,
  'SavingsGoalCalculatorBlock': SavingsGoalCalculatorBlock
}

// Helper function to find the correct calculator type from ContentType array
function getCalculatorType(contentTypeArray) {
  if (!contentTypeArray || !Array.isArray(contentTypeArray)) return null
  
  // Look for calculator block types in the ContentType array
  for (let i = contentTypeArray.length - 1; i >= 0; i--) {
    const type = contentTypeArray[i]
    if (CalculatorComponentMap[type]) {
      return type
    }
  }
  
  // Fallback: look for any type that ends with "CalculatorBlock"
  for (let i = contentTypeArray.length - 1; i >= 0; i--) {
    const type = contentTypeArray[i]
    if (type && type.includes('Calculator')) {
      return type
    }
  }
  
  return null
}

const DesktopCalculatorContainer = ({ calculatorData, calculatorOptions, dropdownHeading, wasTabClicked, activeIndex, setActiveIndex }) => {
  const [options, setOptions] = useState(calculatorOptions)
  const [value, setValue] = useState(calculatorOptions[activeIndex] || calculatorOptions[0])

  useEffect(() => {
    if (calculatorOptions && calculatorOptions.length > 0) {
      setOptions([...calculatorOptions])
      const newValue = calculatorOptions[activeIndex] || calculatorOptions[0]
      setValue(newValue)
    }
  }, [activeIndex, calculatorOptions])

  function onSelectOptionChanged(option) {
    setValue(option)
    setActiveIndex(option.Index)
    updateUrl(option)
  }

  function updateUrl(option) {
    const parsedUrl = new URL(window.location.href)
    // add to search params
    parsedUrl.searchParams.set('CalculatorType', option.ContainerTitle)
    parsedUrl.searchParams.set('Calculator', option.Title)
    parsedUrl.hash = 'Calculators'
    // update url
    window.history.pushState({}, '', parsedUrl)
  }

  useEffect(() => {
    if (wasTabClicked) {
      updateUrl(value)
    }
  }, [value])

  const currentCalculatorData = calculatorData
  const calculatorType = getCalculatorType(currentCalculatorData?.ContentType)
  const CalculatorComponent = CalculatorComponentMap[calculatorType]
  const { BackgroundColor } = currentCalculatorData || {}
  let bgColor = 'bg-transparent'
  switch (BackgroundColor) {
    case 'violet-tint':
      bgColor = 'bg-violet-tint'
      break
    case 'blue-tint':
      bgColor = 'bg-blue-tint'
      break
    case 'emerald-tint':
      bgColor = 'bg-emerald-tint'
      break
    case 'white':
      bgColor = 'bg-white'
      break
  }

  return (
    <div className={`${bgColor} w-screen relative left-1/2 -translate-x-1/2`}>
      <div className="container mx-auto max-w-screen-xl lg:py-8 md:py-7 py-4">
        <div className="xl:ml-24 lg:ml-20 md:ml-12">
          {dropdownHeading && calculatorOptions.length > 1 &&
            <div className='mb-4 text-violet font-bold font-display'>{dropdownHeading}</div>
          }
          {calculatorOptions.length > 1 && (
            <div className='flex gap-8 font-display'>
              <WellbySelect
                showClearButton={false}
                showDivider={false}
                options={options}
                value={value}
                onChange={option => onSelectOptionChanged(option)}
              />
            </div>
          )}
          <div>
            {CalculatorComponent ? (
              <CalculatorComponent data={currentCalculatorData} />
            ) : (
              <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc' }}>
                <h3>No calculator component found</h3>
                <p>Calculator type: {calculatorType}</p>
                <p>ContentType array: [{currentCalculatorData?.ContentType?.join(', ')}]</p>
                <p>Available components: {Object.keys(CalculatorComponentMap).join(', ')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const MobileCalculatorContainer = ({ calculatorContainerData, tabName, activeIndex, setActiveIndex, hasCalculatorFromUrl }) => {
  const [selectedCalculatorIndex, setSelectedCalculatorIndex] = useState(null)
  const [isCalculatorSelected, setIsCalculatorSelected] = useState(false)

  const calculatorTitles = calculatorContainerData.CalculatorsContentArea?.map((item, i) => {
    const overrideTitle = calculatorContainerData.CalculatorDisplayNames?.Value?.[i]?.trim()
    return (overrideTitle && overrideTitle.length > 0) ? overrideTitle : (item.Title || item.Name || `Calculator ${i + 1}`)
  }) || []

  useEffect(() => {
    if (hasCalculatorFromUrl) {
      onCalculatorClicked(activeIndex)
    }
  }, [])

  function onCalculatorClicked(index) {
    if (isCalculatorSelected && index === selectedCalculatorIndex) {
      setIsCalculatorSelected(false)
      setSelectedCalculatorIndex(null)
    } else {
      const parsedUrl = new URL(window.location.href)
      // add to search params
      parsedUrl.searchParams.set('Calculator', calculatorContainerData.CalculatorsContentArea[index].Name)
      parsedUrl.hash = 'Calculators'
      // update url
      window.history.pushState({}, '', parsedUrl)
      setIsCalculatorSelected(true)
      setSelectedCalculatorIndex(index)
      setActiveIndex(index)
    }
  }

  const selectedCalculatorData = calculatorContainerData.CalculatorsContentArea?.[selectedCalculatorIndex]
  const calculatorType = getCalculatorType(selectedCalculatorData?.ContentType)
  const CalculatorComponent = CalculatorComponentMap[calculatorType]
  const { BackgroundColor } = selectedCalculatorData || {}
  let bgColor = 'bg-transparent'
  switch (BackgroundColor) {
    case 'violet-tint':
      bgColor = 'bg-violet-tint'
      break
    case 'blue-tint':
      bgColor = 'bg-blue-tint'
      break
    case 'emerald-tint':
      bgColor = 'bg-emerald-tint'
      break
    case 'white':
      bgColor = 'bg-white'
      break
  }

  return (
    <div>
      <div className='font-bold text-2xl my-4 ml-6 font-display'>{tabName}</div>
      <div>
        {calculatorTitles?.map((item, i) => {
          return (
            <div key={i}>
              <div
                onClick={() => onCalculatorClicked(i)}
                className='flex justify-between p-6 border-b-2 border-gray-300 hover:cursor-pointer hover:bg-gray-100'>
                <div className='font-bold text-base lg:text-lg font-display'>
                  {item}
                </div>
                {isCalculatorSelected && i === selectedCalculatorIndex ?
                  <XMarkSVG className="text-violet" width="20" height="20" /> : <PlusSVG className="text-violet" width="20" height="20" />}
              </div>
              {isCalculatorSelected && i === selectedCalculatorIndex && CalculatorComponent && (
                <div className={`${bgColor} w-screen relative left-1/2 -translate-x-1/2`}>
                  <div className="p-4">
                    <CalculatorComponent data={selectedCalculatorData} />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CalculatorContainerBlock = ({ data, isMobile, tabName, calculatorFromUrl, wasTabClicked }) => {
  const calculators = data?.CalculatorsContentArea || data?.Calculators || data?.calculators

  const { Title, DropdownHeading, CalculatorDisplayNames } = data
  const CalculatorsContentArea = calculators
  const calculatorIndex = CalculatorsContentArea.map((item) => item.Name).indexOf(calculatorFromUrl)
  const hasCalculatorFromUrl = calculatorIndex >= 0
  const [activeIndex, setActiveIndex] = useState(calculatorIndex >= 0 ? calculatorIndex : 0)

  useEffect(() => {
    // Using a timeout to ensure the new iframe has rendered before we try to inject the script.
    const timer = setTimeout(() => {
      gliaInject.runOnce()
    }, 100)

    return () => clearTimeout(timer)
  }, [activeIndex, data])

  let calculatorOptions = CalculatorsContentArea.map((item, i) => {
    let column = `Calculator${i + 1}`
    const overrideTitle = CalculatorDisplayNames?.Value?.[i]?.trim()
    return {
      label: (overrideTitle && overrideTitle.length > 0) ? overrideTitle : (item.Title || item.Name || `Calculator ${i + 1}`),
      value: `${column}${item.Name}`,
      ContainerTitle: Title,
      Title: item.Name,
      Index: i,
    }
  })

  return (
    <div className="overflow-hidden">
      {isMobile === true ?
        <div>
          <MobileCalculatorContainer
            calculatorContainerData={data}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            tabName={tabName}
            hasCalculatorFromUrl={hasCalculatorFromUrl} />
        </div>
        :
        <div>
          <DesktopCalculatorContainer
            calculatorData={CalculatorsContentArea[activeIndex]}
            calculatorOptions={calculatorOptions}
            dropdownHeading={DropdownHeading}
            wasTabClicked={wasTabClicked}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex} />
        </div>}
    </div>
  )
}

export default React.memo(CalculatorContainerBlock)